import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, DatePicker, Descriptions, Select, Space, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import { getUserUsage } from '../../api/users';

const formatTime = (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '暂无记录');
const categories = [
  { value: 'ALL', label: '全部记录' },
  { value: 'LOGIN', label: '登录记录' },
  { value: 'ACTIVITY', label: '功能请求' },
  { value: 'TOKEN_REFRESH', label: '凭证刷新' },
];

export default function UserUsage({ userId }) {
  const [range, setRange] = useState(null);
  const [category, setCategory] = useState('ALL');
  const [pagination, setPagination] = useState({ page: 1, size: 20 });
  const [revision, setRevision] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const from = range?.[0]?.format('YYYY-MM-DD');
  const to = range?.[1]?.format('YYYY-MM-DD');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setReport(null);
    getUserUsage(userId, { from, to, category, ...pagination })
      .then((result) => { if (active) setReport(result); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId, from, to, category, pagination, revision]);

  const columns = [
    { title: '时间', dataIndex: 'occurredAt', width: 180, render: formatTime },
    { title: '行为', dataIndex: 'label', width: 150, render: (label) => <Tag>{label}</Tag> },
    { title: '记录详情', dataIndex: 'detail', render: (value) => <span style={{ overflowWrap: 'anywhere' }}>{value || '—'}</span> },
  ];
  const features = (report?.features || []).filter((item) => !item.eventType.startsWith('LOGIN_') && item.eventType !== 'TOKEN_REFRESH');

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Alert type="info" showIcon title="服务端可观测的使用记录"
        description="此页为历史服务端请求记录，自动采集已停止，历史保留90天。新的授权统计请查看“使用统计”。仅包含已登录用户的成功业务请求，可能含自动刷新、预加载及重试，不等同于真实点击或页面浏览。离线、缓存浏览及流式 AI 对话不在记录范围内。时间按服务器时区展示。" />
      <Card size="small" title="使用概览（不随下方筛选变化）" loading={loading}>
        <Descriptions column={2}>
          <Descriptions.Item label="最近登录（90天内）">{formatTime(report?.summary?.lastLoginAt)}</Descriptions.Item>
          <Descriptions.Item label="最近活跃请求（90天内）">{formatTime(report?.summary?.lastActiveAt)}</Descriptions.Item>
          <Descriptions.Item label="近7天活跃天数">{report?.summary?.activeDays7 ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="近30天活跃天数">{report?.summary?.activeDays30 ?? '—'}</Descriptions.Item>
        </Descriptions>
        <div style={{ color: '#666', fontSize: 12 }}>活跃按有成功请求的自然日计算（含今天），不计凭证刷新；不代表在线时长。</div>
      </Card>
      <Space wrap>
        <DatePicker.RangePicker value={range} onChange={(value) => { setRange(value); setPagination((prev) => ({ ...prev, page: 1 })); }}
          placeholder={['开始日期（默认近30天）', '结束日期']}
          disabledDate={(date) => date.isAfter(dayjs(), 'day') || date.isBefore(dayjs().subtract(90, 'day'), 'day')} />
        <Select aria-label="记录类型" value={category} options={categories} style={{ width: 140 }}
          onChange={(value) => { setCategory(value); setPagination((prev) => ({ ...prev, page: 1 })); }} />
        <Button onClick={() => setRevision((value) => value + 1)} loading={loading}>刷新</Button>
      </Space>
      {error && <Alert type="error" showIcon title="使用记录加载失败，请点击刷新重试" />}
      <Card size="small" title="所选日期内的功能请求次数" loading={loading}>
        <Space wrap>
          {features.length ? features.map((item) => <Tag key={item.eventType}>{item.label}：{item.count} 次</Tag>) : '暂无功能请求记录'}
        </Space>
      </Card>
      <Table size="small" rowKey="id" columns={columns} dataSource={report?.events?.list || []} loading={loading}
        locale={{ emptyText: error ? '加载失败' : '所选范围内暂无记录' }}
        pagination={{ current: pagination.page, pageSize: pagination.size, total: report?.events?.total || 0,
          showSizeChanger: true, pageSizeOptions: [20, 50, 100], showTotal: (total) => `共 ${total} 条`,
          onChange: (page, size) => setPagination({ page, size }) }} />
    </Space>
  );
}
